import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { AppConfig, AppState, MAX_VERSIONS, SCHEMA_VERSION, VersionSnapshot } from "./types/state";
import {
  checkCompatibility,
  isWorkbookReadOnly,
  readDataTable,
  selectRowByKey,
  subscribeSheetChanges,
} from "./lib/office";
import { loadState, saveState } from "./lib/storage";
import { computeDiff, DiffResult } from "./lib/diff";
import { exportDiffPdf, exportFullPdf } from "./lib/pdf";
import { IncompatibleScreen } from "./components/IncompatibleScreen";
import { ConfigScreen } from "./components/ConfigScreen";
import { DiffViewer } from "./components/DiffViewer";
import { SaveVersionDialog } from "./components/SaveVersionDialog";
import { ExportDialog, ExportMode } from "./components/ExportDialog";
import { SettingsPanel } from "./components/SettingsPanel";
import { Toast, ToastKind } from "./components/Toast";

type View = "loading" | "incompatible" | "config" | "main" | "settings";

interface CurrentData {
  columns: string[];
  rows: Record<string, string>[];
}

function defaultVersionLabel(): string {
  const d = new Date();
  return `Version du ${d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })}`;
}

function referenceRows(state: AppState): Record<string, string>[] {
  const last = state.versions[state.versions.length - 1];
  return last?.rows ?? [];
}

export default function App() {
  const [view, setView] = useState<View>("loading");
  const [incompatReason, setIncompatReason] = useState("");
  const [state, setState] = useState<AppState | null>(null);
  const [current, setCurrent] = useState<CurrentData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [toast, setToast] = useState<{ message: string; kind: ToastKind } | null>(null);

  const showToast = useCallback((message: string, kind: ToastKind = "info") => {
    setToast({ message, kind });
  }, []);

  const stateRef = useRef<AppState | null>(null);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const refresh = useCallback(
    async (override?: AppState) => {
      const effective = override ?? stateRef.current;
      if (!effective) return;
      setRefreshing(true);
      try {
        const data = await readDataTable(
          effective.config.dataSheetName,
          effective.config.dataTableName,
          effective.config.columnsToIgnore,
          effective.config.headerRowCount,
          effective.config.topRowsToSkip
        );
        setCurrent(data);
      } catch (e) {
        showToast(
          `Lecture impossible : ${String((e as Error).message ?? e)}. Vérifie la feuille/tableau.`,
          "error"
        );
      } finally {
        setRefreshing(false);
      }
    },
    [showToast]
  );

  const boot = useCallback(async () => {
    const compat = checkCompatibility();
    if (!compat.ok) {
      setIncompatReason(compat.reason ?? "Incompatibilité inconnue.");
      setView("incompatible");
      return;
    }
    try {
      const loaded = await loadState();
      if (!loaded) {
        setView("config");
        return;
      }
      setState(loaded);
      await refresh(loaded);
      setView("main");
    } catch (e) {
      console.error(e);
      showToast(`Erreur chargement : ${String((e as Error).message ?? e)}`, "error");
      setView("config");
    }
  }, [showToast, refresh]);

  useEffect(() => {
    boot();
  }, [boot]);

  useEffect(() => {
    if (view !== "main" || !state) return;
    const sheetName = state.config.dataSheetName;
    let unsubscribe: (() => Promise<void>) | null = null;
    let cancelled = false;

    subscribeSheetChanges(sheetName, () => {
      void refresh();
    })
      .then((fn) => {
        if (cancelled) void fn();
        else unsubscribe = fn;
      })
      .catch((e) => {
        console.error("Auto-refresh subscription failed:", e);
      });

    return () => {
      cancelled = true;
      if (unsubscribe) void unsubscribe();
    };
  }, [view, state?.config.dataSheetName, refresh]);

  const diff: DiffResult | null = useMemo(() => {
    if (!state || !current) return null;
    const ref = referenceRows(state);
    const result = computeDiff(ref, current.rows, state.config.keyColumns, state.config.columnsToIgnore);
    if (import.meta.env.DEV || (window as any).__DIFFAPP_DEBUG__) {
      console.group("[DiffApp] computeDiff");
      console.log("keyColumns:", state.config.keyColumns);
      console.log("headerRowCount:", state.config.headerRowCount);
      console.log("topRowsToSkip:", state.config.topRowsToSkip);
      console.log("columnsToIgnore:", state.config.columnsToIgnore);
      console.log("reference rows:", ref.length, ref);
      console.log("current rows:", current.rows.length, current.rows);
      console.log("result:", result);
      console.groupEnd();
    }
    return result;
  }, [state, current]);

  const handleConfigValidate = async (config: AppConfig, initialRows: Record<string, string>[], initialColumns: string[]) => {
    const snapshot: VersionSnapshot = {
      id: new Date().toISOString(),
      label: `Version initiale — ${new Date().toLocaleDateString("fr-FR")}`,
      savedAt: new Date().toISOString(),
      columns: initialColumns,
      rows: initialRows,
    };
    const newState: AppState = {
      schemaVersion: SCHEMA_VERSION,
      config,
      versions: [snapshot],
    };
    try {
      if (await isWorkbookReadOnly()) {
        showToast("Le classeur est en lecture seule. Impossible de sauvegarder.", "error");
        return;
      }
      await saveState(newState);
      const verify = await loadState();
      if (!verify || verify.versions.length !== newState.versions.length) {
        showToast("Sauvegarde non vérifiée. Réessaie.", "error");
        return;
      }
      setState(newState);
      setCurrent({ columns: initialColumns, rows: initialRows });
      setView("main");
      showToast("Version initiale créée.", "success");
    } catch (e) {
      showToast(`Sauvegarde impossible : ${String((e as Error).message ?? e)}`, "error");
      throw e;
    }
  };

  const handleSaveVersion = async (label: string) => {
    if (!state || !current) return;
    try {
      if (await isWorkbookReadOnly()) {
        showToast("Le classeur est en lecture seule. Impossible de sauvegarder.", "error");
        return;
      }
      const snapshot: VersionSnapshot = {
        id: new Date().toISOString(),
        label,
        savedAt: new Date().toISOString(),
        columns: current.columns,
        rows: current.rows,
      };
      const versions = [...state.versions, snapshot].slice(-MAX_VERSIONS);
      const newState: AppState = { ...state, versions };
      await saveState(newState);
      const verify = await loadState();
      if (!verify || verify.versions.length !== newState.versions.length) {
        showToast("Sauvegarde non vérifiée. Réessaie.", "error");
        return;
      }
      setState(newState);
      setShowSave(false);
      showToast("Version sauvegardée.", "success");
    } catch (e) {
      showToast(`Erreur : ${String((e as Error).message ?? e)}`, "error");
      throw e;
    }
  };

  const handleUpdateConfig = async (config: AppConfig) => {
    if (!state) return;
    const newState: AppState = { ...state, config };
    await saveState(newState);
    setState(newState);
    await refresh(newState);
    showToast("Configuration mise à jour.", "success");
  };

  const handleExport = async (mode: ExportMode) => {
    if (!state || !current || !diff) return;
    const refVersion = state.versions[state.versions.length - 1];
    const refLabel = refVersion?.label ?? "—";
    const listTitle = state.config.dataTableName || state.config.dataSheetName;
    try {
      if (mode === "diff") {
        exportDiffPdf(diff, refLabel, state.config.keyColumns, listTitle);
      } else {
        exportFullPdf(diff, current.rows, current.columns, refLabel, state.config.keyColumns, listTitle);
      }
      setShowExport(false);
      showToast("PDF généré.", "success");
    } catch (e) {
      showToast(`Erreur PDF : ${String((e as Error).message ?? e)}`, "error");
      throw e;
    }
  };

  const handleGoToRow = async (key: string) => {
    if (!state) return;
    try {
      const ok = await selectRowByKey(
        state.config.dataSheetName,
        state.config.dataTableName,
        state.config.keyColumns,
        key,
        state.config.headerRowCount,
        state.config.topRowsToSkip
      );
      if (!ok) showToast("Ligne introuvable.", "error");
    } catch (e) {
      showToast(`Erreur : ${String((e as Error).message ?? e)}`, "error");
    }
  };

  if (view === "loading") {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-stone-600">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  if (view === "incompatible") {
    return <IncompatibleScreen reason={incompatReason} />;
  }

  if (view === "config") {
    return (
      <>
        <ConfigScreen onValidate={handleConfigValidate} />
        {toast && <Toast message={toast.message} kind={toast.kind} onDismiss={() => setToast(null)} />}
      </>
    );
  }

  if (view === "settings" && state) {
    return (
      <>
        <SettingsPanel
          state={state}
          onClose={() => setView("main")}
          onUpdateConfig={handleUpdateConfig}
        />
        {toast && <Toast message={toast.message} kind={toast.kind} onDismiss={() => setToast(null)} />}
      </>
    );
  }

  if (!state || !diff) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-stone-600">
        <Loader2 className="w-4 h-4 animate-spin" /> Préparation…
      </div>
    );
  }

  return (
    <>
      <DiffViewer
        state={state}
        diff={diff}
        refreshing={refreshing}
        onRefresh={() => refresh(state)}
        onSave={() => setShowSave(true)}
        onExport={() => setShowExport(true)}
        onOpenSettings={() => setView("settings")}
        onGoToRow={handleGoToRow}
      />
      {showSave && (
        <SaveVersionDialog
          defaultLabel={defaultVersionLabel()}
          onCancel={() => setShowSave(false)}
          onConfirm={handleSaveVersion}
        />
      )}
      {showExport && (
        <ExportDialog onCancel={() => setShowExport(false)} onConfirm={handleExport} />
      )}
      {toast && <Toast message={toast.message} kind={toast.kind} onDismiss={() => setToast(null)} />}
    </>
  );
}
